import { memo, useCallback, useState, useSyncExternalStore, type MouseEvent } from 'react';
import { DeleteIcon, EditIcon, PushPinIcon } from '@/components/ui/Icons';
import type { AgentChatListItem } from '@/api/client';
import {
  getMessengerDraft,
  subscribeMessengerDraft,
} from '@/components/Messenger/cache/messengerDraftsStore';
import { formatMessengerActivityLabel } from '@/components/Messenger/shared/messengerActivityDateLabel';
import { formatMessengerComposerDraftSubtitle } from '@/components/Messenger/shared/messengerComposerDraftPreview';
import { areAgentChatListItemsEqual } from '@/components/Messenger/shared/agentChatListEquality';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';

export interface AgentChatSidebarRowProps {
  chat: AgentChatListItem;
  active: boolean;
  exiting?: boolean;
  unread: number;
  processing: boolean;
  pinned: boolean;
  onPick: (id: string) => void;
  onDelete: (id: string) => void;
  onRenameChat: (chatId: string, defaultTitle: string) => void;
  onTogglePin: (chatId: string) => void;
}

function AgentChatSidebarRowInner({
  chat,
  active,
  exiting = false,
  unread,
  processing,
  pinned,
  onPick,
  onDelete,
  onRenameChat,
  onTogglePin,
}: AgentChatSidebarRowProps) {
  const [ctxPos, setCtxPos] = useState<{ x: number; y: number } | null>(null);
  const draft = useSyncExternalStore(
    useCallback(
      (onChange) => subscribeMessengerDraft('agent_chat', chat.id, onChange),
      [chat.id]
    ),
    () => getMessengerDraft('agent_chat', chat.id),
    () => undefined
  );

  const closeMenu = useCallback(() => setCtxPos(null), []);

  const handleClick = useCallback(() => {
    onPick(chat.id);
  }, [chat.id, onPick]);

  const handleRowContextMenu = useCallback((e: MouseEvent) => {
    e.preventDefault();
    setCtxPos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleDeleteClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      const label = chat.title || 'Чат';
      if (window.confirm(`Удалить чат «${label}»? Вложения будут удалены из хранилища.`)) {
        onDelete(chat.id);
      }
    },
    [chat.id, chat.title, onDelete]
  );

  const handleRenameClick = useCallback(() => {
    onRenameChat(chat.id, chat.title ?? '');
  }, [chat.id, chat.title, onRenameChat]);

  const draftLine = formatMessengerComposerDraftSubtitle(draft);
  const secondaryLine =
    draftLine ??
    formatMessengerActivityLabel(chat.last_message_at ?? chat.updated_at, { includeTime: true });

  return (
    <div
      className={`chatSidebar__agentChatRow${exiting ? ' chatSidebar__agentChatRow--exiting' : ''}`}
      onContextMenu={handleRowContextMenu}
    >
      <button
        type="button"
        className={`chatSidebar__item chatSidebar__agentChatRow__select${active ? ' chatSidebar__item_active' : ''}`}
        onClick={handleClick}
        title={chat.title || 'Чат'}
      >
        <span className="chatSidebar__itemContent">
          <span className="chatSidebar__itemLabelRow">
            {unread > 0 ? (
              <span className="messengerUnreadBadge chatSidebar__agentChatUnreadLeft">
                {unread > 99 ? '99+' : String(unread)}
              </span>
            ) : null}
            <span className="chatSidebar__itemLabel">{chat.title || 'Чат'}</span>
          </span>
          {secondaryLine ? (
            <span
              className={
                draftLine
                  ? 'chatSidebar__itemSubtitle'
                  : 'chatSidebar__itemRole chatSidebar__itemRole_caps'
              }
            >
              {secondaryLine}
            </span>
          ) : null}
        </span>
        {pinned ? (
          <i className="fa-solid fa-thumbtack chatSidebar__rowPinFa" aria-hidden title="Закреплено" />
        ) : null}
        {processing ? <span className="chatSidebar__agentChatSpinner" aria-label="Астра отвечает" /> : null}
      </button>
      <button
        type="button"
        className="chatSidebar__agentChatDel"
        aria-label={`Удалить чат «${chat.title || 'Чат'}»`}
        title="Удалить"
        onClick={handleDeleteClick}
      >
        <DeleteIcon size={18} className="chatSidebar__agentChatDelIcon" />
      </button>
      <ContextMenu
        isOpen={ctxPos !== null}
        onClose={closeMenu}
        anchorPosition={ctxPos}
        repositionOnScroll={false}
        items={[
          {
            label: pinned ? 'Открепить чат' : 'Закрепить чат',
            icon: <PushPinIcon size={18} />,
            onClick: () => {
              onTogglePin(chat.id);
            },
          },
          {
            label: 'Переименовать',
            icon: <EditIcon size={18} />,
            onClick: () => {
              void handleRenameClick();
            },
          },
        ]}
      />
    </div>
  );
}

function propsEqual(prev: AgentChatSidebarRowProps, next: AgentChatSidebarRowProps): boolean {
  return (
    prev.active === next.active &&
    prev.exiting === next.exiting &&
    prev.unread === next.unread &&
    prev.processing === next.processing &&
    prev.pinned === next.pinned &&
    areAgentChatListItemsEqual(prev.chat, next.chat) &&
    prev.onPick === next.onPick &&
    prev.onDelete === next.onDelete &&
    prev.onRenameChat === next.onRenameChat &&
    prev.onTogglePin === next.onTogglePin
  );
}

export const AgentChatSidebarRow = memo(AgentChatSidebarRowInner, propsEqual);
